import React, {
  FC,
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import LoadingScreen from '../components/LoadingScreen';
import PageLoadError from '../pages/PageLoadError';
import { datadogAddAction } from 'src/pages/tenant/QuestionnaireV4/utils';

interface Props {
  [key: string]: unknown;
}

type ComponentImport = () => Promise<{ default: React.ComponentType<unknown> }>;

// A rejected route-chunk import is almost always a stale document: after a release the
// tab is still pointing at chunk hashes the server no longer serves.
//
// PA-20 — recover **in place**. It used to retry once and then replace the document,
// which threw away every other tab's state and could loop. Now: one silent retry (a
// transient 404 or a dropped connection), then an error page whose **Try again**
// re-runs the import without a reload. The reload is still available to the user, as
// the browser's own button, which is the honest place for it.
const IMPORT_RETRY_DELAY_MS = 300;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const loadWithRecovery = async (
  componentImport: ComponentImport,
  attempt: number,
  onRetry: () => void
): Promise<{ default: React.ComponentType<unknown> }> => {
  try {
    return await componentImport();
  } catch (error) {
    if (attempt === 0) {
      await sleep(IMPORT_RETRY_DELAY_MS);
      return loadWithRecovery(componentImport, attempt + 1, onRetry);
    }

    console.error('[ComponentLoader] chunk load failed', error);
    datadogAddAction(error, 'Chunk Load Error');
    // The error page keeps the shell, the header and the tabs: only this route failed.
    return {
      default: () => <PageLoadError onRetry={onRetry} />,
    };
  }
};

const ComponentLoader = (componentImport: ComponentImport): FC<Props> => {
  const LoadableComponent: FC<Props> = (props) => {
    const { pathname } = useLocation();
    const isPHA = pathname.includes('pronto-admin');
    // PA-20 — bumping this rebuilds the lazy component, which re-runs the import.
    const [attempt, setAttempt] = useState(0);

    const retry = useCallback(() => {
      setAttempt((current) => current + 1);
    }, []);

    const LazyComponent = useMemo(
      () =>
        lazy(() => {
          if (attempt > 0) {
            // Visible in the console so "Try again did nothing" is diagnosable.
            console.info(
              `[ComponentLoader] retrying chunk, attempt ${attempt}`
            );
          }
          return loadWithRecovery(componentImport, 0, retry);
        }),
      // `attempt` is what makes "Try again" create a *new* lazy instance; without it
      // React would hand back the cached rejection and the button would do nothing.
      [attempt, retry]
    );

    return (
      <Suspense
        fallback={
          <LoadingScreen
            sx={{
              ...(!isPHA && {
                top: 0,
                left: 0,
                width: '100%',
                zIndex: 9999,
                position: 'fixed',
              }),
            }}
          />
        }
      >
        <LazyComponent {...props} />
      </Suspense>
    );
  };

  return LoadableComponent;
};

export default ComponentLoader;
